import React, { Component } from 'react'
import classNames from 'classnames'
import {
    Button,
    InputGroup,
    MenuItem,
    Popover,
    Switch,
    Tooltip,
    Tag,
} from '@blueprintjs/core'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {faXmark} from '@fortawesome/pro-regular-svg-icons/faXmark'
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass'
import { faSlidersUp } from '@fortawesome/pro-solid-svg-icons/faSlidersUp'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'

import ButtonSelect from 'src/components/ButtonSelect'
import StatusIcon from 'src/components/StatusIcon'
import {
    filterActions,
    statusOptions,
} from 'src/store/modules/runbooks/actions'
import { setDepthLevel, getRunbook } from 'src/store/modules/runbooks'
import { selectWindow } from 'src/store/modules/ui/runbook/controls'

import styles from './filterControls.module.scss'

export const LOGS = {
    HISTORY: 'history-log',
    COMMENT: 'comments-log',
    ISSUE: 'issues-log',
    DECISIONS: 'decisions-log',
    ACTIVITIES: 'activities-log',
}

class FilterControls extends Component {
    initialQuery = {
        statusesFilter: [],
        ownedByMeFilter: null, // will be an object
        ownedByMyTeamFilter: null,
        simplifiedView: null,
        drRecoveryType: null,
    }

    state = {
        selectedDepthLevel: 0,
        selectedView: {
            name: 'Actions List',
            window: 'actions-list',
        },
        isOpenSettingsPopover: false,
        query: { ...this.initialQuery },
        queryBeforeOpenSettings: null,
        isFiltering: false,
        isFilterApplied: false,
    }

    cleanQuery() {
        const { query } = this.state

        return Object.keys(query).reduce((obj, key) => {
            if (Array.isArray(query[key]) && !query[key].length) {
                return obj
            }

            if (query[key]) {
                obj[key] = query[key]
            }

            return obj
        }, {})
    }

    filter() {
        const { runbook, actions } = this.props
        const cleanedQuery = this.cleanQuery()

        actions.filterActions(runbook.id, cleanedQuery)

        if (Object.keys(cleanedQuery).length === 0) {
            this.setState({ isFiltering: false })
        }
    }

    handleSelectDepthLevel(item) {
        const { actions, runbook } = this.props

        this.setState({ selectedDepthLevel: item })
        actions.setDepthLevel(runbook.id, item)
    }

    handleSelectActionView(item) {
        const { actions } = this.props

        this.setState({ selectedView: item })

        if (item.window) {
            actions.selectWindow(item.window)
        }
    }

    handleFilterChange = (name, value, callback) => {
        const { query } = this.state

        if (name === 'statusesFilter') {
            const newStatusesFilter = [...query.statusesFilter]

            if (newStatusesFilter.includes(value)) {
                newStatusesFilter.splice(newStatusesFilter.indexOf(value), 1)
            } else {
                newStatusesFilter.push(value)
            }

            return this.setState(
                {
                    query: {
                        ...query,
                        statusesFilter: newStatusesFilter,
                    },
                },
                callback
            )
        }

        this.setState(
            {
                query: { ...query, [name]: value },
                isFiltering: true,
            },
            callback
        )
    }

    handleOpenSettingsPopover = () => {
        const {
            isOpenSettingsPopover,
            queryBeforeOpenSettings,
            query,
        } = this.state

        if (!isOpenSettingsPopover) {
            this.setState({
                isOpenSettingsPopover: !isOpenSettingsPopover,
                queryBeforeOpenSettings: {
                    ...query,
                },
            })
        } else {
            this.setState({
                isOpenSettingsPopover: !isOpenSettingsPopover,
                query: { ...queryBeforeOpenSettings },
                queryBeforeOpenSettings: null,
            })
        }
    }

    handleApplyFilter = () => {
        const { actions, runbook } = this.props
        actions.setDepthLevel(runbook.id, runbook.maxDepth)
        this.setState(
            {
                isOpenSettingsPopover: false,
                selectedDepthLevel: runbook.maxDepth,
                isFilterApplied: true,
            },
            this.filter
        )
    }

    handleResetFilter = () => {
        this.setState(
            {
                query: { ...this.initialQuery },
                isOpenSettingsPopover: false,
                isFilterApplied: false,
            },
            this.filter
        )
    }

    _renderSelectDepth() {
        const { selectedDepthLevel } = this.state
        const { runbook } = this.props

        return (
            <ButtonSelect
                label="Show depth"
                // generate an array containing 1 to the maximum depth of the runbook
                items={[...Array(runbook.maxDepth + 1).keys()]}
                itemRenderer={(item) => (
                    <MenuItem
                        intent="none"
                        active={selectedDepthLevel === item}
                        key={item}
                        text={`Level ${item}`}
                        onClick={() => this.handleSelectDepthLevel(item)}
                    />
                )}
                selectedLabel={`Level ${selectedDepthLevel}`}
                className={styles.levelSelect}
            />
        )
    }

    _renderSelectActionsList() {
        const { selectedView } = this.state
        const { runbook, selectedWindow } = this.props

        const items = [
            {
                name: 'Actions List',
                window: 'actions-list',
            },
            {
                name: 'History Log',
                window: runbook.activeSession && LOGS.HISTORY,
            },
            {
                name: 'Comment Log',
                window: runbook.activeSession && 'comments-log',
            },
            {
                name: 'Issue Log',
                window: runbook.activeSession && LOGS.ISSUE,
            },
            {
                name: 'Decisions Log',
                window: runbook.activeSession && LOGS.DECISIONS,
            },
            {
                name: 'Activities Log',
                window: runbook.activeSession && LOGS.ACTIVITIES,
            },
            {
                name: 'Graph',
                url: `/runbooks/graph/${runbook.id}/`,
                icon: 'document-open',
            },
        ]

        return (
            <ButtonSelect
                label="View"
                items={items}
                itemRenderer={(item) => (
                    <MenuItem
                        key={item.name}
                        active={selectedWindow === item.window}
                        disabled={!item.url && !item.window}
                        icon={item.icon}
                        text={item.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={item.url}
                        onClick={() => this.handleSelectActionView(item)}
                    />
                )}
                selectedLabel={selectedView.name}
            />
        )
    }

    _renderClearInputIcon(filedToClear) {
        const { query } = this.state

        return query[filedToClear] ? (
            <FontAwesomeIcon
                icon={faXmark}
                className={styles.clearInputIcon}
                onClick={() =>
                    this.handleFilterChange(filedToClear, '', this.filter)
                }
            />
        ) : null
    }

    _renderSearchInActions() {
        const ClearIcon = () => {
            if (!this.props.searchFilter) {
                return null
            }

            return (
                <FontAwesomeIcon
                    icon={faXmark}
                    className={styles.clearInputIcon}
                    onClick={() => this.props.handleSearch('')}
                />
            )
        }

        return (
            <InputGroup
                name="searchFilter"
                value={this.props.searchFilter}
                placeholder="Search in actions"
                leftElement={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                rightElement={ <ClearIcon />}
                className={styles.searchActions}
                onChange={(e) => this.props.handleSearch(e.target.value)}
            />
        )
    }

    _renderSearchByOwner() {
        const ClearIcon = () => {
            if (!this.props.ownerFilter) {
                return null
            }

            return (
                <FontAwesomeIcon
                    icon={faXmark}
                    className={styles.clearInputIcon}
                    onClick={() => this.props.handleSearchOwner('')}
                />
            )
        }

        return (
            <InputGroup
                name="ownerFilter"
                value={this.props.ownerFilter}
                placeholder="Search by owner"
                leftElement={<FontAwesomeIcon icon={faUser} />}
                rightElement={ <ClearIcon />}
                onChange={(e) => this.props.handleSearchOwner(e.target.value)}
            />
        )
    }

    _renderSettingsButton() {
        const { isOpenSettingsPopover, query } = this.state
        const { currentUser, runbook } = this.props
        const nrOfSelectedFilters =
            query.statusesFilter.length +
            (query.ownedByMeFilter ? 1 : 0) +
            (query.ownedByMyTeamFilter ? 1 : 0)+
            (query.simplifiedView ? 1 : 0)

        return (
            <Popover
                minimal
                className={styles.settingsPopover}
                position="bottom-right"
                usePortal={false}
                isOpen={isOpenSettingsPopover}
                disabled={runbook.isEditing}
                autoFocus={false}
                content={
                    <div className={styles.settingsPopoverContent}>
                        <div className={styles.settingsPopoverRow}>
                            <Switch
                                checked={!!query.ownedByMeFilter}
                                label="Owned by me"
                                large
                                alignIndicator="right"
                                onChange={(e) =>
                                    this.handleFilterChange(
                                        'ownedByMeFilter',
                                        e.target.checked ? currentUser : false
                                    )
                                }
                            />
                        </div>
                        <div className={styles.settingsPopoverRow}>
                            <Switch
                                checked={!!query.ownedByMyTeamFilter}
                                label="Owned by my team(s)"
                                large
                                alignIndicator="right"
                                onChange={(e) =>
                                    this.handleFilterChange(
                                        'ownedByMyTeamFilter',
                                        e.target.checked ? currentUser : false
                                    )
                                }
                            />
                        </div>
                          <div className={styles.settingsPopoverRow}>
                            <Switch
                                checked={!!query.simplifiedView}
                                label="Simplified view"
                                large
                                alignIndicator="right"
                                onChange={(e) =>
                                    this.handleFilterChange(
                                        'simplifiedView',
                                        e.target.checked ? currentUser : false
                                    )
                                }
                            />
                        </div>
                        <div
                            className={classNames(
                                styles.settingsPopoverRow,
                                styles.filterStatus
                            )}
                        >
                            <p>Select status(es)</p>
                            {statusOptions.ids.map((status) => (
                                <Tooltip
                                    content={statusOptions.byId[status].value}
                                    position="bottom"
                                    key={status}
                                >
                                    <button
                                        className={classNames(
                                            styles.statusFilterButton
                                        )}
                                        onClick={() =>
                                            this.handleFilterChange(
                                                'statusesFilter',
                                                status
                                            )
                                        }
                                    >
                                        <StatusIcon
                                            status={status}
                                            notSelected={
                                                !query.statusesFilter.includes(
                                                    status
                                                )
                                            }
                                            changeable
                                        />
                                    </button>
                                </Tooltip>
                            ))}
                        </div>
                        <div>
                            <Button
                                intent="primary"
                                large
                                onClick={this.handleApplyFilter}
                                className={styles.applyFilterButton}
                            >
                                Apply filters
                                {nrOfSelectedFilters > 0 ? (
                                    <span
                                        className={styles.nrOfSelectedFilters}
                                    >
                                        {nrOfSelectedFilters}
                                    </span>
                                ) : null}
                            </Button>
                            {nrOfSelectedFilters > 0 ? (
                                <Button
                                    intent="lynch-underline"
                                    minimal
                                    className={styles.resetButton}
                                    onClick={this.handleResetFilter}
                                >
                                    Reset
                                </Button>
                            ) : null}
                        </div>
                    </div>
                }
            >
                <Button
                    intent={isOpenSettingsPopover ? 'primary' : 'tarawera'}
                    className={styles.settingsButton}
                    disabled={runbook.isEditing}
                    onClick={this.handleOpenSettingsPopover}
                >
                    <FontAwesomeIcon
                        icon={isOpenSettingsPopover ? faXmark : faSlidersUp}
                        className={styles.settingsIcon}
                    />
                </Button>
            </Popover>
        )
    }

    _renderResultsInPage() {
        const { currentMatch = 0 } = this.props

        const {
            goToPreviousMatch,
            goToNextMatch,
            matchedRows = [],
        } = this.props

        if (!matchedRows?.length) {
            return null
        }

        return (
            <div className={styles.queryResultsContainer}>
                <span className={styles.results}>Results</span>
                <span>
                    {currentMatch + 1}/{matchedRows?.length}
                </span>
                <Button icon="chevron-up" onClick={goToPreviousMatch} minimal />
                <Button icon="chevron-down" onClick={goToNextMatch} minimal />
            </div>
        )
    }

    render() {
        const { query } = this.state;
        const nrOfSelectedFilters =
            query.statusesFilter.length +
            (query.ownedByMeFilter ? 1 : 0) +
            (query.ownedByMyTeamFilter ? 1 : 0) +
            (query.simplifiedView ? 1 : 0);

        const { isFilterApplied } = this.state;
        return (
            <div className={styles.root}>
                <div>
                    {this._renderSelectDepth()}
                    {this._renderSelectActionsList()}
                </div>

                <div className={styles.filtersContainer}>
                    {this._renderResultsInPage()}
                    <div className={styles.inputsContainer}>
                        {this._renderSearchInActions()}
                        {this._renderSearchByOwner()}
                        {this._renderSettingsButton()}
                    </div>
                </div>
                {isFilterApplied && nrOfSelectedFilters > 0 && (
                    <div className={styles.appliedFiltersBadgeContainer}>
                        <Tag intent="primary" round>
                            {nrOfSelectedFilters}
                        </Tag>
                    </div>
                )}
            </div>
        )
    }
}

const mapStateToProps = (state) => ({
    currentUser: state.app.user,
    runbook: getRunbook(state, state.ui.runbook.activeRunbook),
    selectedWindow: state.ui.runbook.controls.selectedWindow,
})

const mapDispatchToProps = (dispatch) => ({
    dispatch,
    actions: bindActionCreators(
        {
            setDepthLevel,
            selectWindow,
            filterActions,
        },
        dispatch
    ),
})

export default connect(mapStateToProps, mapDispatchToProps)(FilterControls)
